import {ChangeEvent, Dispatch, SetStateAction, useCallback} from 'react';
import InputMask from 'react-input-mask';
import * as yup from 'yup';
import {
  Box,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {ISponsorDetailsProp} from '../../dto/Sponsor.dto';

type Props = {
  sponsorDetails: ISponsorDetailsProp;
  setSponsorDetails: Dispatch<SetStateAction<ISponsorDetailsProp>>;
  setSponsorDetailsValidate: (select: boolean) => void;
};

const sponsorDetailSchema = yup.object().shape({
  sponsor_name: yup
    .string()
    .required('Sponsor name is required')
    .min(3, 'Sponsor name is too short')
    .max(50, 'Sponsor name is too long'),
  main_contact: yup
    .string()
    .required('Contact person name is required')
    .min(3, 'Contact person name is too short')
    .max(50, 'Contact person name is too long'),
  email_address: yup
    .string()
    .required('Email is required')
    .email('Invalid email address')
    .max(150, 'Email is too long')
    .matches(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      'Invalid email address',
    ),
  phone_number: yup
    .string()
    .required('Phone number is required')
    .matches(
      /^\(\d{3}\) \d{3}-\d{4}$/,
      'Invalid phone number. Please use the format (999) 999-9999',
    ),
});

const AboutSponsor = () => {
  return (
    <>
      <Typography variant='h5' className='text-white font-bold mb-8'>
        About Your Sponsor
      </Typography>

      <Box>
        <p>
          All sponsor commercials are 15-30 seconds in length. Please fill out
          the following form to either share information about your sponsor or
          share their contact information with our team and we will work
          directly with them.
        </p>
        <p>
          Commercial will take 5-14 business days to complete. Please ensure
          your sponsors are checking their primary inbox and spam folders for
          information from <i>production@neptunenow.com</i>.
        </p>
        <p>
          We look forward to working with you and your sponsors to create a
          professional radio commercial!
        </p>
      </Box>
    </>
  );
};

export const SponsorDetails = ({
  sponsorDetails,
  setSponsorDetails,
  setSponsorDetailsValidate,
}: Props) => {
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const {name, value} = event.target;
      setSponsorDetails((prevDetails: ISponsorDetailsProp) => {
        const updatedVal = {
          ...prevDetails,
          [name]: value,
        };
        sponsorDetailSchema
          .validate(updatedVal)
          .then(() => {
            setSponsorDetailsValidate(true);
          })
          .catch(() => {
            setSponsorDetailsValidate(false);
          });
        return updatedVal;
      });
    },
    [setSponsorDetails, setSponsorDetailsValidate],
  );
  return (
    <Card>
      <Typography variant='h5' className='text-white'>
        About Your Sponsor
      </Typography>
      <CardContent>
        <Typography variant='h6' gutterBottom>
          Tell Us About Your Sponsor!!
        </Typography>
        <Stack direction='column'>
          <TextField
            required
            id='sponsor-name'
            name='sponsor_name'
            value={sponsorDetails.sponsor_name}
            onChange={handleChange}
            label='Sponsor/Company Name'
            helperText='Min(3)-Max(50) Character Allowed'
          />
          <TextField
            required
            id='main-contact'
            name='main_contact'
            value={sponsorDetails.main_contact}
            onChange={handleChange}
            label="Sponsor's Main Contact"
            helperText='Min(3)-Max(50) Character Allowed'
          />
          <TextField
            required
            id='main-email'
            name='email_address'
            value={sponsorDetails.email_address}
            onChange={handleChange}
            label="Main Contact's Email Address"
            helperText='emailaddress@domain.com'
          />
          <InputMask
            mask='(999) 999-9999'
            disabled={false}
            value={sponsorDetails.phone_number}
            onChange={handleChange}>
            <TextField
              required
              disabled={false}
              type='text'
              fullWidth
              name='phone_number'
              label="Main Contact's Phone Number"
              id='mobile_number'
              placeholder='(e.g.: 60.133.365/0001-16)'
              helperText='(999) 999-9999'
            />
          </InputMask>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AboutSponsor;
